import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { CommonController } from '@classes/common.controller';
import { CodelistDisplayModel } from '@common/models/common/CodelistDisplay.model';
import { UserModel } from '@common/models/User.model';
import { AttachmentService } from '@common/services/attachment.service';
import { marker } from '@common/translate-marker';
import { TranslateService } from '@ngx-translate/core';
import { FileRestrictions } from '@progress/kendo-angular-upload';
import { ChatService } from '@services/chat.service';
import { FileDownloadService } from '@services/file-download.service';
import { ModalService } from '@services/modal.service';
import { ToastService } from '@services/toast.service';
import { currentUserSubject } from '@services/user.service';
import { ChatDetailsDisplayModel } from '../../../models/Chat/ChatDetailsDisplayModel';

@Component({
    selector: 'app-chat-window-expand-note',
    templateUrl: './chat-window-expand-note.component.html',
    styleUrls: ['./chat-window-expand-note.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChatWindowExpandNoteComponent extends CommonController implements OnInit {
    @Input() openNoteId: number | null = null;
    @Input() isNew: boolean;
    @Output() closeModal = new EventEmitter<boolean>();
    @ViewChild('responseTextarea', { static: false }) responseTextarea: ElementRef<HTMLTextAreaElement>;

    isBusy = false;

    rawData: ChatDetailsDisplayModel | null = null;

    flattenedData: {
        id: number;
        remark: string;
        forUser: any;
        createdBy: any;
        forOrganization: CodelistDisplayModel;
        chatNoteType: CodelistDisplayModel;
        createdByOrganization: CodelistDisplayModel;
        createdDate: Date;
        isNew: boolean;
        viewDate: Date;
        childrenNumber: number;
        attachmentId: number;
        attachmentName: string;
        active: boolean;
        depth: number;
    }[] = [];

    selectedReplyNote: number = null;
    selectedEditNote: number = null;
    selectedDeleteNote: number = null;

    thisUser: UserModel;

    loadingChatTypes = false;
    chatTypes: CodelistDisplayModel[];
    allOrgs: CodelistDisplayModel[];
    allUsers: {
        id: number;
        displayName: string;
        orgId: string;
    }[];

    isAllowedToEditUser = false;
    isAllowedToEditOrg = false;

    userSelection: any[] = [];
    orgSelection: any[] = [];

    selectedChatType = null;
    selectedUser: any = null;
    selectedOrg: any = null;

    editedNote = '';
    newNote = '';

    openedModalConfirmDeleteNote = false;

    attachmentFileUploadRestrictions: FileRestrictions = {
        maxFileSize: 5242880,
        allowedExtensions: ['.pdf', '.jpg', '.png', '.csv', '.xls', '.txt', '.xlsx', '.doc', '.docx', '.jpeg', '.gif']
    };

    fileToSend: string = null;
    fileName: string = null;

    removingExistingAttachmentOnEdit = false;
    editingAttachment = false;
    editedFileToSend: string = null;
    editedFileName: string = null;

    constructor(
        protected dialogService: ModalService,
        protected translateService: TranslateService,
        private service: ChatService,
        private fileDownloadService: FileDownloadService,
        private attachmentService: AttachmentService,
        private toastyService: ToastService
    ) {
        super(dialogService, translateService);
    }

    ngOnInit(): void {
        this.isBusy = true;
        this.loadAttachmentSettings();
        this.selectedReplyNote = this.openNoteId;
        this.thisUser = currentUserSubject.getValue();
        if (!this.selectedEditNote) {
            this.getChatTypes().add(() => {
                this.loadChatNotes();
            });
        }
    }

    @HostListener('document:keydown.escape')
    onEscapeKey(): void {
        this.closeModalWindow();
    }

    onTextareaKeydown(event: KeyboardEvent): void {
        if (event.ctrlKey && event.key === 'Enter') {
            event.preventDefault(); // Prevent default behavior (new line)
            this.submitReply();
        }
    }

    private focusResponseTextarea(): void {
        // Check if the response area should be visible based on the same conditions as the template
        if (
            !this.isBusy &&
            this.rawData !== null &&
            (this.rawData.watcher === null || !this.userBelongsToOrganization(this.rawData.watcher.id))
        ) {
            if (this.responseTextarea && this.responseTextarea.nativeElement) {
                try {
                    // Use a small delay to ensure DOM is fully rendered
                    setTimeout(() => {
                        this.responseTextarea.nativeElement.focus();
                        this.responseTextarea.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 50);
                } catch (error) {
                    console.log('Could not focus textarea:', error);
                }
            }
        }
    }

    private loadChatNotes() {
        this.service.getChatNoteDetails(this.openNoteId).subscribe((response) => {
            this.rawData = response;
            this.flattenedData = [];

            this.flattenData(this.rawData, 0);

            if (!this.flattenedData[0].active) {
                this.closeModal.emit(true);
                return;
            }

            // this.flattenedData.sort(function (a, b) {
            //     return a.createdDate < b.createdDate ? 0 : 1; // gets the correct order to display chat notes by created date
            // });
            this.isBusy = false;

            // Focus the response textarea after data is loaded and view is updated
            setTimeout(() => {
                this.focusResponseTextarea();
            }, 100);

            this.selectedChatType = this.flattenedData[0].chatNoteType;
            this.chatTypeSelectionChange(this.selectedChatType);
            this.selectedOrg = this.flattenedData[0].forOrganization;
            this.organizationSelectionChange(this.selectedOrg);
            this.selectedUser = this.flattenedData[0].forUser;
        });
    }

    private flattenData(note: ChatDetailsDisplayModel, depth: number = 0) {
        this.flattenedData.push({
            id: note.id,
            forOrganization: note.forOrganization,
            chatNoteType: note.chatNoteType,
            createdDate: note.createdDate,
            forUser: note.forUser,
            createdBy: note.createdBy,
            remark: note.remark,
            createdByOrganization: note.createdByOrganization,
            isNew: note.isNew,
            childrenNumber: note.childNotes.length,
            attachmentId: note.attachmentId,
            attachmentName: note.attachmentName,
            active: note.active,
            viewDate: note.viewDate,
            depth: depth
        });
        note.childNotes.forEach((cn) => {
            this.flattenData(cn, depth + 1);
        });
    }

    getIndentationStyle(depth: number): any {
        if (depth === 0) {
            return {};
        }

        return {
            'margin-left': `${depth * 10}px`,
            'border-left': `3px solid ${this.getIndentationColor(depth)}`,
            position: 'relative'
        };
    }

    getIndentationColor(depth: number): string {
        const colors = ['#4a90ee', '#4a90aa', '#4a9077', '#4a9033', '#4a9000'];
        return colors[(depth - 1) % colors.length];
    }

    public getCorrectClasses(noteId): string {
        const note = this.flattenedData.find((n) => n.id === noteId);
        let classes = '';

        if (note.createdBy.id === this.thisUser.id || note.createdByOrganization.id === this.thisUser.organization.id) {
            classes += 'myNote ';
        } else {
            classes += 'otherNote ';
        }

        if (note.isNew) {
            classes += ' newNote';
        } else {
            classes += ' viewedNote';
        }

        return classes;
    }

    public selectReplyNote(noteId) {
        this.selectedReplyNote = noteId;

        const thisNote = this.flattenedData.find((d) => d.id === noteId);
        this.selectedChatType = thisNote.chatNoteType;
        this.chatTypeSelectionChange(this.selectedChatType);

        this.selectedOrg = thisNote.createdByOrganization;
        this.organizationSelectionChange(this.selectedOrg);

        this.selectedUser = thisNote.createdBy;
    }

    public selectEditNote(noteId) {
        this.selectedEditNote = noteId;
        const thisNote = this.flattenedData.find((d) => d.id === noteId);
        this.selectedChatType = thisNote.chatNoteType;
        this.chatTypeSelectionChange(this.selectedChatType);
        this.selectedOrg = thisNote.forOrganization;
        this.organizationSelectionChange(this.selectedOrg);
        this.selectedUser = thisNote.forUser;
        this.editedNote = thisNote.remark;
    }

    public selectRemoveNote(noteId) {
        if (this.canRemoveChatNote(noteId)) {
            this.selectedDeleteNote = noteId;
            this.openedModalConfirmDeleteNote = true;
        }
    }

    public selectUnreadNote(chatNoteId: number) {
        this.service.markChatNoteAsUnread(chatNoteId).subscribe(() => {
            this.closeModalWindow();
        });
    }

    public confirmRemoveNote() {
        this.service
            .removeChatNote({
                chatNoteId: this.selectedDeleteNote
            })
            .subscribe(() => {
                this.openedModalConfirmDeleteNote = false;
                this.loadChatNotes();
            });
    }

    public closeRemoveNote() {
        this.openedModalConfirmDeleteNote = false;
    }

    public submitReply() {
        this.service
            .submitReply({
                parentId: this.selectedReplyNote,
                remark: this.newNote,
                type: this.selectedChatType?.id,
                file: this.fileToSend,
                fileName: this.fileName
            })
            .subscribe(() => {
                this.selectedChatType = null;
                this.selectedOrg = null;
                this.selectedUser = null;
                this.newNote = '';
                this.loadChatNotes();
            });
    }

    public clearNewNote() {
        this.newNote = '';
    }

    public saveEdit() {
        this.service
            .editNote({
                chatNoteId: this.selectedEditNote,
                newMessage: this.editedNote,
                type: this.selectedChatType.id,
                org: this.selectedOrg?.id,
                user: this.selectedUser?.id,
                file: this.editedFileToSend,
                fileName: this.editedFileName,
                isEditingAttachment: this.editingAttachment
            })
            .subscribe(() => {
                this.selectedEditNote = null;
                this.editedNote = '';
                this.selectedChatType = null;
                this.selectedOrg = null;
                this.selectedUser = null;
                this.editedFileToSend = null;
                this.editedFileName = null;
                this.editingAttachment = false;
                this.loadChatNotes();
            });
    }

    public cancelEdit() {
        this.selectedEditNote = null;
        this.editingAttachment = false;
        this.editedFileName = null;
        this.editedFileToSend = null;
    }

    canRemoveChatNote(noteId): boolean {
        return this.flattenedData.find((fd) => fd.id === noteId).childrenNumber === 0;
    }

    canEditChatNote(noteId: number): boolean {
        const note = this.flattenedData.find((fd) => fd.id === noteId);
        if (!note) {
            return false;
        }

        // If the note has no children (no replies), it can be edited
        if (note.childrenNumber === 0) {
            return true;
        }

        // If the note has children, only allow editing if it's the last message in the entire thread
        // Find the last note in the flattened data (which is the last chronologically due to the tree traversal)
        const lastNote = this.flattenedData[this.flattenedData.length - 1];
        return note.id === lastNote.id;
    }

    private getChatTypes() {
        this.loadingChatTypes = true;
        return this.service.getChatTypes().subscribe((response) => {
            this.chatTypes = response.chatTypes;
            this.allOrgs = response.orgs;
            this.allUsers = response.users;
            this.loadingChatTypes = false;
        });
    }

    private loadAttachmentSettings(): void {
        this.attachmentService.getAttachmentSettingsForUpload().subscribe((response) => {
            this.attachmentFileUploadRestrictions = response as FileRestrictions;
        });
    }

    public chatTypeSelectionChange($event) {
        this.userSelection = [];
        this.orgSelection = [];
        this.selectedUser = null;
        this.selectedOrg = null;

        if ($event === undefined) {
            this.organizationSelectionChange(undefined);
            return;
        }

        const user = currentUserSubject.getValue();
        const userOrg = user.organization.id.toString();
        const userId = user.id;

        if ($event.id === 'FOR_ALL') {
            this.isAllowedToEditUser = false;
            this.isAllowedToEditOrg = false;
            this.userSelection = [];
            this.orgSelection = [];
        } else if ($event.id === 'INT_FOR_ME') {
            this.isAllowedToEditOrg = false;
            this.isAllowedToEditUser = false;
            this.userSelection = this.allUsers.filter((u) => u.id === userId);
            this.orgSelection = this.allOrgs.filter((o) => o.id === userOrg);
            this.selectedUser = this.userSelection[0];
            this.selectedOrg = this.orgSelection[0];
        } else if ($event.id === 'INT_FOR_MY_ORG') {
            this.isAllowedToEditOrg = false;
            this.isAllowedToEditUser = false;
            this.userSelection = [];
            this.orgSelection = this.allOrgs.filter((o) => o.id === userOrg);
            this.selectedOrg = this.orgSelection[0];
        } else if ($event.id === 'FOR_ORG') {
            this.isAllowedToEditOrg = true;
            this.isAllowedToEditUser = false;
            this.userSelection = [];
            this.orgSelection = this.allOrgs;
        } else if ($event.id === 'FOR_USER') {
            this.isAllowedToEditUser = false;
            this.isAllowedToEditOrg = true;
            this.orgSelection = this.allOrgs;
            this.userSelection = [];
        }
    }

    public organizationSelectionChange($event) {
        if ($event === undefined) {
            this.userSelection = [];
            this.isAllowedToEditUser = false;
            this.selectedUser = null;
            return;
        }

        if (this.selectedChatType.id === 'FOR_USER') {
            this.isAllowedToEditUser = true;
            this.userSelection = this.allUsers.filter((u) => u.orgId === $event.id);
        }
    }

    public removeAttachmentOnEdit() {
        this.editingAttachment = true;
    }

    public displayUserSelection(user: any): string {
        return user.firstName + ' ' + user.lastName + ' (' + user.userName + ')';
    }

    attachmentDownload(id: number): void {
        this.service.downloadAttachment(id).subscribe((response) => {
            this.fileDownloadService.handleAttachmentDownload(response);
        });
    }

    fileSelected($event, forReply: boolean) {
        $event.files.forEach((element) => {
            if (
                element.size > this.attachmentFileUploadRestrictions.maxFileSize ||
                !this.attachmentFileUploadRestrictions.allowedExtensions.some((ext) => ext == element.extension)
            ) {
                this.toastyService.error(
                    this.translateService.instant(marker('Invalid attachment')) + ' - ' + element.name
                );
                return;
            }
            const raw = element.rawFile;
            if (raw) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.documentOnLoad(e.target.result.toString(), raw.name, forReply);
                };
                reader.readAsDataURL(element.rawFile);
            }
        });
    }

    documentOnLoad(file: string, name: string, forReply: boolean) {
        if (forReply) {
            this.fileToSend = file;
            this.fileName = name;
        } else {
            this.editedFileToSend = file;
            this.editedFileName = name;
            this.editingAttachment = true;
        }
    }

    fileRemoved($event, forReply: boolean) {
        if (forReply) {
            this.fileToSend = null;
            this.fileName = null;
        } else {
            this.editedFileToSend = null;
            this.editedFileName = null;
            this.editingAttachment = true;
        }
    }

    closeModalWindow() {
        this.closeModal.emit(false);
    }

    getHeaderInfo() {
        if (this.flattenedData && this.flattenedData.length > 0) {
            const firstNote = this.flattenedData[0];
            return {
                title: firstNote.remark || this.translateService.instant(marker('Note')),
                sender: firstNote.createdBy?.displayName || this.translateService.instant(marker('Unknown')),
                organization:
                    firstNote.createdByOrganization?.name ||
                    this.translateService.instant(marker('Unknown Organization')),
                type: firstNote.chatNoteType?.name || this.translateService.instant(marker('Unknown Type')),
                forUser: firstNote.forUser?.displayName || this.translateService.instant(marker('Unknown')),
                forOrganization:
                    firstNote.forOrganization?.name || this.translateService.instant(marker('Unknown Organization')),
                isUnread: firstNote.isNew,
                date: firstNote.createdDate,
                hasMultipleNotes: this.flattenedData.length > 1,
                noteCount: this.flattenedData.length
            };
        }
        return {
            title: 'Note',
            sender: 'Unknown',
            organization: 'Unknown Organization',
            type: 'Unknown Type',
            forUser: 'Unknown',
            forOrganization: 'Unknown Organization',
            isUnread: false,
            date: new Date(),
            hasMultipleNotes: false,
            noteCount: 0
        };
    }

    getUserInitials(fullName: string): string {
        if (!fullName) return '?';

        const names = fullName.trim().split(' ');
        if (names.length === 1) {
            return names[0].charAt(0).toUpperCase();
        }

        return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    }

    getReplyToNoteInfo(): string {
        if (!this.selectedReplyNote || !this.flattenedData) return '';

        const replyToNote = this.flattenedData.find((note) => note.id === this.selectedReplyNote);
        if (!replyToNote) return '';

        const senderName = replyToNote.createdBy?.displayName || 'Unknown User';
        const notePreview = replyToNote.remark?.substring(0, 50) || '';
        const truncated = replyToNote.remark?.length > 50 ? '...' : '';

        return `${senderName}: "${notePreview}${truncated}"`;
    }
}
